import { Head } from "@inertiajs/react"
import { useForm } from "@inertiajs/react"
import { useRef } from "react"
import type { FormEvent, ReactNode } from "react"

import InputError from "@/components/input-error"
import TextLink from "@/components/text-link"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Spinner } from "@/components/ui/spinner"
import AuthLayout from "@/layouts/auth-layout"
import { newIdentityPasswordResetPath, signInPath, signUpPath } from "@/routes"

export default function Login() {
  const { data, setData, post, processing, errors, reset } = useForm({
    email: "",
    password: "",
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    post(signInPath(), { onSuccess: () => reset("password") })
  }

  return (
    <AuthLayout
      title="Log in to your account"
      description="Enter your email and password below to log in"
    >
      <Head title="Log in" />
      <div className="flex flex-col gap-6">
        <form onSubmit={submit} className="flex flex-col gap-6">
          <div className="grid gap-6">
            <div className="grid gap-2">
              <Label htmlFor="email">Email address</Label>
              <Input
                id="email"
                type="email"
                required
                autoFocus
                tabIndex={1}
                autoComplete="email"
                placeholder="email@example.com"
                value={data.email}
                onChange={(e) => setData("email", e.target.value)}
              />
              <InputError messages={errors.email} />
            </div>

            <div className="grid gap-2">
              <div className="flex items-center">
                <Label htmlFor="password">Password</Label>
                <TextLink
                  href={newIdentityPasswordResetPath()}
                  className="ml-auto text-sm"
                  tabIndex={5}
                >
                  Forgot password?
                </TextLink>
              </div>
              <Input
                id="password"
                type="password"
                required
                tabIndex={2}
                autoComplete="current-password"
                placeholder="Password"
                value={data.password}
                onChange={(e) => setData("password", e.target.value)}
              />
              <InputError messages={errors.password} />
            </div>

            <Button
              type="submit"
              className="mt-4 w-full"
              tabIndex={4}
              disabled={processing}
            >
              {processing && <Spinner />}
              Log in
            </Button>
          </div>

          <div className="text-muted-foreground text-center text-sm">
            Don&apos;t have an account?{" "}
            <TextLink href={signUpPath()} tabIndex={5}>
              Sign up
            </TextLink>
          </div>
        </form>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <Separator />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background text-muted-foreground px-2">
              Or continue with
            </span>
          </div>
        </div>

        <OmniAuthButton provider="developer">
          Sign in with OmniAuth
        </OmniAuthButton>
      </div>
    </AuthLayout>
  )
}

function OmniAuthButton({
  provider,
  children,
}: {
  provider: string
  children: ReactNode
}) {
  const formRef = useRef<HTMLFormElement>(null)

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = formRef.current
    if (!form) return

    const csrfToken = document.querySelector<HTMLMetaElement>(
      'meta[name="csrf-token"]',
    )?.content
    if (csrfToken) {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "authenticity_token"
      input.value = csrfToken
      form.appendChild(input)
    }
    form.submit()
  }

  return (
    <form
      ref={formRef}
      method="post"
      action={`/auth/${provider}`}
      onSubmit={handleSubmit}
    >
      <Button type="submit" variant="outline" className="w-full">
        {children}
      </Button>
    </form>
  )
}
